import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Briefcase, Users, Shield, TrendingUp, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Careers = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    position: 'Subcontractor',
    experience_years: '',
    available_start_date: '',
    has_transportation: false,
    authorized_to_work: true,
    cover_letter: ''
  });

  const positions = [
    'Subcontractor',
    'Employee'
  ];

  const benefits = [
    { icon: Users, title: 'Great Team Culture', desc: 'Join a tight-knit team that supports one another' },
    { icon: Shield, title: 'Fully Insured', desc: 'Complete protection while you work' },
    { icon: TrendingUp, title: 'Growth Opportunities', desc: 'Build your career as we expand across Georgia' },
    { icon: Briefcase, title: 'Flexible Schedule', desc: 'Work-life balance that fits your needs' }
  ];

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.position) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/job-applications`, form);
      setSubmitted(true);
      toast.success('Application submitted! We\'ll be in touch soon.');
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FDFDFC' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-12 max-w-2xl w-full mx-8 text-center"
          style={{ border: '1px solid #E2E8F0' }}
        >
          <CheckCircle className="w-20 h-20 mx-auto mb-6" style={{ color: '#10B981' }} />
          <h1 className="text-3xl font-medium mb-4" style={{ fontFamily: 'Outfit', color: '#0F172A' }}>
            Application Received!
          </h1>
          <p className="text-base mb-8" style={{ color: '#475569' }}>
            Thank you for applying to join Radius Cleaning Company. Our team will review your application and reach out within 5-7 business days.
          </p>
          <button
            data-testid="back-home-from-careers-btn"
            onClick={() => navigate('/')}
            className="px-8 py-3 rounded-full font-medium hover:opacity-90 transition-opacity"
            style={{ background: '#0284C7', color: '#FFFFFF' }}
          >
            Back to Home
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#FDFDFC' }}>
      <header className="sticky top-0 z-50 glassmorphism border-b" style={{ borderColor: '#E2E8F0', background: 'rgba(255,255,255,0.85)' }}>
        <div className="container mx-auto px-8 py-0 flex items-center justify-between">
          <img
            src="https://customer-assets.emergentagent.com/job_schedule-clean-3/artifacts/ps55wz7g_6CC27561-FF2C-4C06-AEF1-E32A8E83E69A.png"
            alt="Radius Cleaning Company"
            className="h-72 w-auto object-contain"
          />
          <button
            data-testid="back-to-home-btn"
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-4 py-2 rounded-full hover:bg-gray-100 transition-colors"
            style={{ color: '#475569' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </div>
      </header>

      <section className="py-24" style={{ background: '#F3F4F6' }}>
        <div className="container mx-auto px-8">
          <div className="max-w-3xl">
            <span className="text-xs font-bold tracking-[0.2em] uppercase mb-4 inline-block" style={{ color: '#0284C7' }}>
              JOIN OUR TEAM
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light mb-6" style={{ fontFamily: 'Outfit', color: '#0F172A' }}>
              Build Your Career With Radius
            </h1>
            <p className="text-base leading-relaxed" style={{ color: '#475569' }}>
              We're growing across Georgia and looking for dedicated, hard-working people who take pride in transforming spaces. If you value quality, integrity, and being part of a growing team — we want to meet you.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24" style={{ background: '#FDFDFC' }}>
        <div className="container mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8"
                style={{ border: '1px solid #E2E8F0' }}
                data-testid={`benefit-${i}`}
              >
                <b.icon className="w-10 h-10 mb-4" style={{ color: '#0284C7' }} />
                <h3 className="text-lg font-medium mb-2" style={{ color: '#0F172A' }}>{b.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#475569' }}>{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" style={{ background: '#F3F4F6' }}>
        <div className="container mx-auto px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-xs font-bold tracking-[0.2em] uppercase mb-4 inline-block" style={{ color: '#0284C7' }}>
                APPLY NOW
              </span>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-medium mb-4" style={{ fontFamily: 'Outfit', color: '#0F172A' }}>
                Tell Us About Yourself
              </h2>
              <p className="text-base" style={{ color: '#475569' }}>
                Fill out the form below and our hiring team will review your application.
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl p-10"
              style={{ border: '1px solid #E2E8F0' }}
              data-testid="job-application-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0F172A' }}>Full Name *</label>
                  <input
                    data-testid="applicant-name-input"
                    type="text"
                    value={form.full_name}
                    onChange={(e) => handleChange('full_name', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl"
                    style={{ border: '1px solid #E2E8F0' }}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0F172A' }}>Email *</label>
                  <input
                    data-testid="applicant-email-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl"
                    style={{ border: '1px solid #E2E8F0' }}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0F172A' }}>Phone *</label>
                  <input
                    data-testid="applicant-phone-input"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl"
                    style={{ border: '1px solid #E2E8F0' }}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0F172A' }}>Position *</label>
                  <select
                    data-testid="applicant-position-select"
                    value={form.position}
                    onChange={(e) => handleChange('position', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl"
                    style={{ border: '1px solid #E2E8F0' }}
                  >
                    {positions.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0F172A' }}>Years of Experience</label>
                  <select
                    data-testid="applicant-experience-select"
                    value={form.experience_years}
                    onChange={(e) => handleChange('experience_years', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl"
                    style={{ border: '1px solid #E2E8F0' }}
                  >
                    <option value="">Select...</option>
                    <option value="None">No experience</option>
                    <option value="<1 year">Less than 1 year</option>
                    <option value="1-3 years">1-3 years</option>
                    <option value="3-5 years">3-5 years</option>
                    <option value="5+ years">5+ years</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0F172A' }}>Available Start Date</label>
                  <input
                    data-testid="applicant-start-date-input"
                    type="date"
                    value={form.available_start_date}
                    onChange={(e) => handleChange('available_start_date', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl"
                    style={{ border: '1px solid #E2E8F0' }}
                  />
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    data-testid="has-transportation-checkbox"
                    type="checkbox"
                    checked={form.has_transportation}
                    onChange={(e) => handleChange('has_transportation', e.target.checked)}
                    className="w-5 h-5"
                    style={{ accentColor: '#0284C7' }}
                  />
                  <span className="text-base" style={{ color: '#0F172A' }}>I have reliable transportation</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    data-testid="authorized-to-work-checkbox"
                    type="checkbox"
                    checked={form.authorized_to_work}
                    onChange={(e) => handleChange('authorized_to_work', e.target.checked)}
                    className="w-5 h-5"
                    style={{ accentColor: '#0284C7' }}
                  />
                  <span className="text-base" style={{ color: '#0F172A' }}>I am authorized to work in the United States</span>
                </label>
              </div>

              <div className="mb-8">
                <label className="block text-sm font-medium mb-2" style={{ color: '#0F172A' }}>
                  Tell us why you'd be a great fit (optional)
                </label>
                <textarea
                  data-testid="cover-letter-input"
                  value={form.cover_letter}
                  onChange={(e) => handleChange('cover_letter', e.target.value)}
                  rows={5}
                  className="w-full px-4 py-3 rounded-xl"
                  style={{ border: '1px solid #E2E8F0' }}
                  placeholder="Share your story, experience, or why you're excited to join us..."
                />
              </div>

              <button
                data-testid="submit-application-btn"
                type="submit"
                disabled={submitting}
                className="w-full px-8 py-4 rounded-full font-medium text-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ background: '#0284C7', color: '#FFFFFF' }}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Application'
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
